import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import './AboutUs.css';
import tenantStorage from '../../utils/tenantStorage';

const AboutUs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check login status on component mount
  useEffect(() => {
    const accessToken = tenantStorage.getItem('accessToken');
    setIsLoggedIn(!!accessToken);
  }, []);

  const handleButtonClick = () => {
    if (isLoggedIn) {
      // Go back to previous page or dashboard if no previous page
      navigate(-1);
    } else {
      // Navigate to login page
      navigate('/login');
    }
  };

  return (
    <div className="about-container">
      <div className="about-header">
        <h1>About Us</h1>
        <div className="about-subtitle">Empowering PG and Hostel Management Through Innovation</div>
      </div>
      
      <div className="about-content">
        <section className="about-section">
          <h2>Welcome to RentTick</h2>
          <p>
            RentTick is a comprehensive PG (Paying Guest) and hostel management system developed by 
            HeliaTick for our clients. Our platform provides a complete solution to manage day-to-day 
            activities for running PGs and hostels, designed specifically for hostel management users 
            who need efficient tools to streamline their operations.
          </p>
        </section>

        <section className="about-section">
          <h2>Our Mission</h2>
          <p>
            To revolutionize PG and hostel management by providing innovative, reliable, and user-friendly 
            solutions that enable hostel operators to focus on what matters most - creating exceptional 
            living experiences for their residents while optimizing operational efficiency and reducing 
            administrative burden.
          </p>
        </section>

        <section className="about-section">
          <h2>Core Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <h3>👥 Paying Guest Management</h3>
              <p>Complete PG lifecycle management from registration to checkout with detailed profiles, documentation, and resident history tracking.</p>
            </div>
            <div className="feature-card">
              <h3>🏠 Building Management</h3>
              <p>Comprehensive building administration with room layouts, facility management, and real-time availability tracking.</p>
            </div>
            <div className="feature-card">
              <h3>🛏️ Bed Management & Maintenance</h3>
              <p>Smart bed inventory system with condition tracking, maintenance scheduling, and availability optimization.</p>
            </div>
            <div className="feature-card">
              <h3>📋 Allocation Management</h3>
              <p>Efficient bed allocation system with automated assignment, transfer capabilities, and occupancy optimization for maximum utilization.</p>
            </div>
            <div className="feature-card">
              <h3>🔧 Complaint Management</h3>
              <p>Streamlined complaint handling system with ticket tracking, priority management, and resolution workflow for resident issues.</p>
            </div>
            <div className="feature-card">
              <h3>👷 Staff Management</h3>
              <p>Complete staff administration with role management, task assignment, and performance tracking for efficient operations.</p>
            </div>
            <div className="feature-card">
              <h3>💰 Invoicing & Payments</h3>
              <p>Monthly invoice generation and payment collection system with automated billing, payment tracking, and financial reporting.</p>
            </div>
          </div>
        </section>

        <section className="about-section">
          <h2>Why Choose RentTick?</h2>
          <div className="benefits-list">
            <div className="benefit-item">
              <h3>🚀 PG & Hostel Focused</h3>
              <p>Specifically designed for PG and hostel operations with features tailored to accommodation management needs.</p>
            </div>
            <div className="benefit-item">
              <h3>📱 User-Friendly Design</h3>
              <p>Intuitive interface designed for hostel management users and residents with minimal learning curve.</p>
            </div>
            <div className="benefit-item">
              <h3>🔒 Secure & Reliable</h3>
              <p>Enterprise-grade security with regular backups and data protection measures for sensitive resident information.</p>
            </div>
            <div className="benefit-item">
              <h3>📈 Scalable Solution</h3>
              <p>Grows with your business from single hostels to large-scale PG operations across multiple locations.</p>
            </div>
            <div className="benefit-item">
              <h3>🌐 Cloud-Based</h3>
              <p>Access your data anytime, anywhere with our cloud-based infrastructure designed for 24/7 operations.</p>
            </div>
            <div className="benefit-item">
              <h3>💬 Dedicated Support</h3>
              <p>HeliaTick support team ready to assist you with any questions or issues specific to your PG/hostel needs.</p>
            </div>
          </div>
        </section>

        <section className="about-section">
          <h2>Our Technology</h2>
          <p>
            RentTick is built using modern web technologies to ensure performance, security, and scalability 
            specifically for PG and hostel operations. Our platform leverages cloud infrastructure, real-time 
            data processing, and mobile-responsive design to deliver a seamless experience for hostel management users.
          </p>
          <div className="tech-highlights">
            <div className="tech-item">✓ Cloud-based architecture</div>
            <div className="tech-item">✓ Real-time data synchronization</div>
            <div className="tech-item">✓ Mobile-responsive design</div>
            <div className="tech-item">✓ Automated backup systems</div>
            <div className="tech-item">✓ Advanced security protocols</div>
            <div className="tech-item">✓ PG/Hostel focused workflows</div>
          </div>
        </section>

        <section className="about-section">
          <h2>Who We Serve</h2>
          <div className="target-audience">
            <div className="audience-card">
              <h3>🏢 Hostel Operators</h3>
              <p>From small boutique hostels to large hostel chains, we provide comprehensive tools to manage every aspect of your hostel operations.</p>
            </div>
            <div className="audience-card">
              <h3>🏠 PG Owners</h3>
              <p>Paying Guest accommodation providers looking to streamline operations and improve resident management efficiency.</p>
            </div>
            <div className="audience-card">
              <h3>🎓 Student Accommodation</h3>
              <p>Educational institutions and private operators managing student hostels and accommodation facilities.</p>
            </div>
            <div className="audience-card">
              <h3>🏘️ Multi-Location Operators</h3>
              <p>Businesses managing multiple PG and hostel properties seeking centralized management solutions.</p>
            </div>
          </div>
        </section>

        <section className="about-section">
          <h2>Development Team</h2>
          <p>
            RentTick is proudly developed and maintained by <strong>HeliaTick</strong>, a technology company 
            dedicated to creating innovative solutions for the PG and hostel management industry. Our team combines 
            years of experience in software development with deep understanding of accommodation management challenges.
          </p>
        </section>

        <section className="about-section">
          <h2>Our Commitment</h2>
          <p>
            We are committed to continuous improvement and innovation in PG and hostel management. Our development 
            team regularly updates the platform with new features, security enhancements, and performance optimizations 
            based on client feedback and industry best practices.
          </p>
          <div className="commitment-items">
            <div className="commitment-item">🔄 Regular updates and improvements</div>
            <div className="commitment-item">🛡️ Ongoing security enhancements</div>
            <div className="commitment-item">📞 Responsive customer support</div>
            <div className="commitment-item">📚 Comprehensive documentation and training</div>
            <div className="commitment-item">🌟 Client-driven feature development</div>
          </div>
        </section>
      </div>

      <div className="about-footer">
        <RTButton variant="primary" onClick={handleButtonClick}>
          {isLoggedIn ? 'Close' : 'Login to Get Started'}
        </RTButton>
      </div>
    </div>
  );
};

export default AboutUs;
